import React from 'react'
import { Route, Switch, useRouteMatch, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import AppSnackbarProvider from 'components/app-snackbar-provider'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import SchoolOutlinedIcon from 'components/material-symbol/icons/School'
import HomeIcon from 'components/material-symbol/icons/Home'
import OpenInNewIcon from 'components/material-symbol/icons/OpenInNew'
import ConfirmationNumberOutlinedIcon from 'components/material-symbol/icons/ConfirmationNumber'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import Shell from 'components/app-shell'
import PGAIcon from 'images/pga-logo-navy.png'
import { withProtectedRoute, roles } from 'lib/auth'
import themes, { extendTheme } from 'themes'
import ConsumerMessages from 'pages/club/messages'
import MyCoach from 'pages/club/my-coach'
import MyEvents from 'pages/club/my-events'
import MyLessonPacks from 'pages/club/my-lesson-packs'
import MyPgaBannerAd from 'components/smart-banner-ads/my-pga-banner-ad'
import GiftLessonPack from 'pages/club/my-lesson-packs/gift'
import AcceptLessonCredit from 'pages/club/my-lesson-packs/accept-lesson-credit'
import { useUnreadMessagesCount } from 'lib/stream-messaging/hooks'
import InlineCountBadge from 'components/inline-count-badge'
import { useIsMobile } from 'lib/hooks'
import { useDefaultLauncherVisibility } from 'lib/support'

const ClubRoutes = () => {
  const { path } = useRouteMatch()
  const { pathname } = useLocation()
  const isMobile = useIsMobile()
  const unreadMessagesCount = useUnreadMessagesCount()

  const shouldShowDefaultLauncher =
    !isMobile &&
    !pathname.includes('/messages') &&
    !pathname.includes('/accept-lesson-credit')

  useDefaultLauncherVisibility(shouldShowDefaultLauncher)

  const menuItems = [
    {
      icon: <HomeIcon />,
      label: 'Dashboard',
      path: `${path}`,
      exact: true,
      tooltip: 'My Coach',
    },
    {
      icon: <ConfirmationNumberOutlinedIcon size={1} />,
      label: 'Lesson Packs',
      path: `${path}/lesson-packs`,
      tooltip: 'Lesson Packs',
    },
    {
      icon: <CelebrationOutlinedIcon />,
      label: 'My Events',
      path: `${path}/events`,
      tooltip: 'My Events',
    },
    {
      icon: <MessageOutlinedIcon />,
      label: (
        <InlineCountBadge count={unreadMessagesCount} tone="error">
          Messaging
        </InlineCountBadge>
      ),
      path: `${path}/messages`,
      tooltip: 'Messaging',
    },
    {
      icon: <SchoolOutlinedIcon />,
      label: 'Golf Tips',
      path: 'https://www.pga.com/search?searchText=golf%20tips&offset=0',
      external: true,
      annotation: <OpenInNewIcon style={{ fontSize: '16px' }} />,
      tooltip: 'Golf Tips',
    },
  ]

  return (
    <ThemeProvider
      theme={extendTheme(themes.pgaCoach)(createTheme(themes.common))}
    >
      <Helmet>
        <title>MyPGA Clubhouse</title>
        <meta name="description" content="MyPGA Clubhouse" />
        <link rel="icon" href="/pga-coach-favicon.ico" />
      </Helmet>
      <AppSnackbarProvider
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MyPgaBannerAd />
        <Shell
          logo={{
            src: PGAIcon,
            alt: 'PGA logo',
            width: '48px',
            mobileWidth: '48px',
          }}
          menuItems={menuItems}
          showBrandSwitcher={false}
        >
          <Switch>
            <Route exact path={`${path}`} component={MyCoach} />
            <Route
              exact
              path={`${path}/lesson-packs`}
              component={MyLessonPacks}
            />
            <Route
              exact
              path={`${path}/events/:eventSlug?`}
              component={MyEvents}
            />
            <Route
              exact
              path={`${path}/messages/:externalId/new-conversation`}
              component={ConsumerMessages}
            />
            <Route
              exact
              path={`${path}/messages/:channelId?`}
              component={ConsumerMessages}
            />
            <Route
              exact
              path={`${path}/lesson-packs/:lessonPackId/gift`}
              component={GiftLessonPack}
            />
            <Route
              exact
              path={`${path}/lesson-packs/:lessonPackId/accept-lesson-credit`}
              component={AcceptLessonCredit}
            />
          </Switch>
        </Shell>
      </AppSnackbarProvider>
    </ThemeProvider>
  )
}

export default withProtectedRoute(ClubRoutes, roles.CONSUMER)
